import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, User, Lock, Wifi, Shield, Zap, Globe2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useBranding } from '../contexts/BrandingContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper: parse hex / fallback
const toHex = (c, fallback) => {
  if (!c || typeof c !== 'string') return fallback;
  const s = c.trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return s;
  return fallback;
};

// Lighten / darken util — naive but cukup utk overlay & glow
const shade = (hex, amount) => {
  try {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const num = parseInt(full, 16);
    const r = Math.min(255, Math.max(0, ((num >> 16) & 0xff) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
    const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  } catch {
    return hex;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useApp();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Per-host branding cache to ELIMINATE flicker on refresh.
  // Cache key is bound to the current hostname so swapping between
  // radiuslink.id and app.kumarahotspot.com never reuses stale brand.
  const _LB_CACHE_KEY = 'rl_login_brand_v2:' + ((typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : 'default');
  const _readLoginBrandCache = () => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(_LB_CACHE_KEY) : null;
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch { return null; }
  };
  const _writeLoginBrandCache = (b) => {
    try { localStorage.setItem(_LB_CACHE_KEY, JSON.stringify(b)); } catch {}
  };
  // Hostname-based synchronous brand hint — used as INITIAL theme color
  // so primary/accent matches tenant from the very first render (no blue flash
  // on Kumara custom domain when localStorage cache is empty).
  const _hostBrandHint = (() => {
    const host = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname.toLowerCase() : '';
    if (host.includes('kumarahotspot')) {
      return {
        custom_domain: host,
        primary_color: '#F97316',
        secondary_color: '#DC2626',
        // No brand_name / logo here — brand area stays in skeleton until
        // the real branding API responds. Only theme colors are hinted.
        _hint_only: true,
      };
    }
    return null;
  })();
  const [localBranding, setLocalBranding] = useState(() => _readLoginBrandCache() || _hostBrandHint);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginSuccess, setLoginSuccess] = useState(null); // { name, role } when sign-in succeeded

  // Force dark theme + clear leaked CSS variables on Login mount.
  // After logout from dashboard (light mode), the `dark` class may have been
  // removed from <html>, and BrandingContext's applyTheme may have set the
  // --primary-color / --secondary-color CSS variables to a different tenant's
  // colour. This caused the post-logout login page to render with broken
  // typography/contrast (washed-out heading, solid-white inputs), forcing
  // the user to manually refresh. We restore a known-good state synchronously,
  // additionally observe class mutations (AppContext re-applies it whenever
  // its `theme` state runs through useEffect) and revert on unmount so the
  // dashboard's preferred theme is honoured again after sign-in.
  useEffect(() => {
    const html = document.documentElement;
    const hadDark = html.classList.contains('dark');
    const prevPrimary = html.style.getPropertyValue('--primary-color');
    const prevSecondary = html.style.getPropertyValue('--secondary-color');

    const enforceDark = () => {
      if (!html.classList.contains('dark')) html.classList.add('dark');
    };
    enforceDark();
    html.style.removeProperty('--primary-color');
    html.style.removeProperty('--secondary-color');

    // AppContext's useEffect re-applies dark/light class whenever its state
    // changes. Watch for class mutations and re-force dark to defeat the
    // logout race condition that previously required a manual refresh.
    let mo = null;
    try {
      mo = new MutationObserver(() => enforceDark());
      mo.observe(html, { attributes: true, attributeFilter: ['class'] });
    } catch (_e) { /* MutationObserver not available */ }

    return () => {
      try { mo && mo.disconnect(); } catch (_e) {}
      if (!hadDark) html.classList.remove('dark');
      if (prevPrimary) html.style.setProperty('--primary-color', prevPrimary);
      if (prevSecondary) html.style.setProperty('--secondary-color', prevSecondary);
    };
  }, []);

  // Fetch public branding (host-aware first, fallback default)
  useEffect(() => {
    const fetchPublicBranding = async () => {
      try {
        const host = (window.location.hostname || '').toLowerCase();
        if (host && host !== 'localhost' && !/^[0-9.]+$/.test(host)) {
          const r = await axios.get(`${API_URL}/api/public/branding-by-host`, {
            params: { host },
            timeout: 4000,
          });
          if (r.data?.success && r.data?.data && r.data.data.tenant_name) {
            const d = r.data.data;
            const hostBrand = {
              company_name: d.company_name || d.tenant_name,
              tagline: d.tagline || '',
              logo_url: d.logo_url || null,
              logo_base64: null,
              primary_color: d.primary_color,
              secondary_color: d.secondary_color,
              tenant_name: d.tenant_name,
              custom_domain: d.custom_domain,
            };
            setLocalBranding(hostBrand);
            _writeLoginBrandCache(hostBrand);
            return;
          }
        }
      } catch (e) {
        /* fallback below */
      }
      try {
        const response = await axios.get(`${API_URL}/api/v2/public/branding`);
        if (response.data.success) setLocalBranding(response.data.data);
      } catch {}
    };
    fetchPublicBranding();
  }, []);

  // IMPORTANT: never fall back to global "branding" context here — it ships with
  // RADIUSLINK defaults and would briefly flash before localBranding resolves
  // on custom tenant domains. Render brand area only when localBranding is set.
  const displayBranding = localBranding || {};
  const isBrandingReady = !!(localBranding && !localBranding._hint_only);
  const logoUrl = displayBranding.logo_base64
    ? (displayBranding.logo_base64.startsWith('data:')
      ? displayBranding.logo_base64
      : `data:image/png;base64,${displayBranding.logo_base64}`)
    : (displayBranding.logo_portal_url || displayBranding.logo_invoice_url || displayBranding.logo_url);

  // Resolve theme colors dari branding tenant. Tenant KUMARA: orange/red corporate.
  // Default: blue/violet RadiusLink.
  const isKumara = (displayBranding.tenant_name || '').toLowerCase().includes('pusaka kreasi')
    || (displayBranding.custom_domain || '').includes('kumarahotspot');

  const theme = useMemo(() => {
    // Per-tenant accent. Kumara → oranye-merah corporate.
    if (isKumara) {
      return {
        primary: '#F97316',     // Oranye Kumara
        primaryDark: '#C2410C',
        accent: '#DC2626',      // Merah accent
        // 2026-07-14 (Redesign): brighter, warmer gradient (navy → plum → warm-brown)
        bgFrom: '#1a1832',      // Deep indigo-navy
        bgVia: '#2d1c3a',       // Plum purple
        bgTo: '#3a1e15',        // Warm brown (orange-tinted)
      };
    }
    const p = toHex(displayBranding.primary_color, '#7c3aed');
    const s = toHex(displayBranding.secondary_color, '#0891b2');
    return {
      primary: s,
      primaryDark: shade(s, -30),
      accent: p,
      bgFrom: '#1e1b3a',
      bgVia: '#252148',
      bgTo: '#1a1a3a',
    };
  }, [isKumara, displayBranding.primary_color, displayBranding.secondary_color]);

  // Haptic feedback helper. navigator.vibrate is supported on most Android
  // browsers (Chrome/Firefox/Edge mobile). iOS Safari ignores it silently —
  // which is safe; we never throw, never block UI.
  const haptic = (pattern) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern);
    } catch (_e) { /* noop */ }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(loginData.username, loginData.password);
    if (result.success) {
      const userRole = result.user?.role;
      const userName = result.user?.full_name || result.user?.name || result.user?.username || '';
      // Positive short pulse — confirms successful authentication on mobile.
      haptic(40);
      // Show success splash for premium "perceived speed" then navigate.
      setLoginSuccess({ name: userName, role: userRole });
      setTimeout(() => {
        if (userRole === 'super_admin' || userRole === 'superadmin') navigate('/app/superadmin-home');
        else navigate('/app/dashboard');
      }, 700);
    } else {
      // Warning pattern (double tap) — signals failed sign-in.
      haptic([50, 60, 50]);
      toast.error(result.error);
      setLoading(false);
    }
  };

  const checkIfSuperAdmin = (username) => {
    setIsSuperAdmin(username.includes('superadmin') || username.includes('@system.com'));
  };

  const brandTitle = isSuperAdmin ? 'RADIUSLINK' : (displayBranding.company_name || 'ISP Billing');
  const brandTagline = isSuperAdmin
    ? 'Super Admin Portal'
    : (displayBranding.tagline || 'High Speed Internet Unlimited');

  // Login success splash (mirrors logout splash for symmetric premium feel)
  const successSplash = loginSuccess ? (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center login-success-splash"
      style={{
        background: `radial-gradient(circle at 50% 50%, ${theme.bgVia} 0%, ${theme.bgFrom} 70%, #000 100%)`,
      }}
      data-testid="login-success-splash"
    >
      <div className="login-success-inner flex flex-col items-center gap-6 px-8 text-center">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={displayBranding.company_name || 'Logo'}
            className="h-16 w-auto max-w-[260px] object-contain"
            style={{ filter: 'drop-shadow(0 6px 24px rgba(0,0,0,0.5))' }}
          />
        ) : (
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}
          >
            <Wifi className="w-8 h-8 text-white" />
          </div>
        )}
        <div className="flex flex-col items-center gap-1">
          <div className="text-white text-base sm:text-lg font-semibold tracking-tight">
            Selamat datang{loginSuccess.name ? `, ${loginSuccess.name}` : ''}
          </div>
          <div className="text-slate-300/80 text-xs sm:text-sm">Menyiapkan dashboard Anda...</div>
        </div>
        <div
          className="w-44 h-1 rounded-full overflow-hidden mt-1"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <div
            className="h-full login-success-bar"
            style={{
              background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`,
              boxShadow: `0 0 12px ${theme.primary}`,
            }}
          />
        </div>
      </div>
      <style>{`
        @keyframes loginSuccessFade {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes loginSuccessRise {
          0%   { opacity: 0; transform: translateY(10px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes loginSuccessBar {
          0%   { width: 0%; }
          100% { width: 100%; }
        }
        .login-success-splash { animation: loginSuccessFade 0.28s ease-out forwards; }
        .login-success-inner { animation: loginSuccessRise 0.4s 0.05s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .login-success-bar { animation: loginSuccessBar 0.6s 0.1s cubic-bezier(0.4, 0.0, 0.2, 1) forwards; }
      `}</style>
    </div>
  ) : null;

  return (
    <>
    {successSplash}
    <div
      className="min-h-screen min-h-[100dvh] relative flex flex-col lg:flex-row overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${theme.bgFrom} 0%, ${theme.bgVia} 50%, ${theme.bgTo} 100%)`,
      }}
    >
      {/* Background accents */}
      <div
        className="absolute -top-32 -left-32 w-[36rem] h-[36rem] rounded-full opacity-[0.28] blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${theme.primary} 0%, transparent 70%)` }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[36rem] h-[36rem] rounded-full opacity-[0.22] blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${theme.accent} 0%, transparent 70%)` }}
      />
      {/* 2026-07-14: Removed grid pattern; using aurora blobs + soft mesh */}
      {/* Extra aurora blob (center) for warmth */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[42rem] h-[42rem] rounded-full opacity-[0.10] blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${theme.primary} 0%, transparent 70%)` }}
      />
      {/* Subtle noise texture (SVG data-URI, no grid lines) for depth */}
      <div
        className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='400' height='400' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* LEFT PANEL — Brand Showcase */}
      <div className="relative z-10 hidden lg:flex flex-col justify-between w-full lg:w-[58%] xl:w-[60%] px-12 xl:px-20 py-14">
        {/* Top - Platform brand (hardcoded RadiusLink for uniform look across all tenants) */}
        <div className="login-fade-in" style={{ animationDelay: '0.1s' }} data-testid="login-platform-logo">
          <img
            src="/radiuslink-billing-logo.png"
            alt="RadiusLink - ISP Billing Management"
            className="h-16 xl:h-20 w-auto max-w-[320px] object-contain"
            style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.4))' }}
          />
        </div>

        {/* Middle - Hero Text */}
        <div className="space-y-5 login-fade-in" style={{ animationDelay: '0.25s' }}>
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wider uppercase"
            style={{
              background: `${theme.primary}22`,
              border: `1px solid ${theme.primary}55`,
              color: theme.primary,
            }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: theme.primary }}
            />
            Enterprise ISP Billing Platform
          </div>

          <h1 className="text-white font-extrabold text-4xl xl:text-6xl leading-[1.05] tracking-tight min-h-[1.2em]">
            {isBrandingReady ? brandTitle.split(' ').map((w, i) => (
              <span key={i} className={i === 0 ? '' : 'block xl:inline xl:ml-3'}>
                {i === 0 ? <span style={{ color: theme.primary }}>{w}</span> : w}{' '}
              </span>
            )) : (
              <span className="inline-block bg-white/[0.04] rounded-lg" style={{minWidth: '320px', minHeight: '1em'}}>&nbsp;</span>
            )}
          </h1>

          <p className="text-slate-300/90 text-base xl:text-lg max-w-xl leading-relaxed min-h-[1.6em]">
            {isBrandingReady ? (<>{brandTagline}. Kelola pelanggan, tagihan, dan jaringan dari satu dashboard terpadu — kapan saja, di mana saja.</>) : null}
          </p>

          {/* Feature list */}
          <div className="grid grid-cols-2 gap-3 max-w-xl pt-4">
            {[
              { icon: Zap, text: 'Auto-Reminder & Isolir' },
              { icon: Shield, text: 'MikroTik & RADIUS Sync' },
              { icon: Globe2, text: 'GenieACS TR-069' },
              { icon: CheckCircle2, text: 'Multi-Tenant Ready' },
            ].map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 text-slate-200/90 text-sm"
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                  style={{ background: `${theme.primary}22`, color: theme.primary }}
                >
                  <f.icon className="w-3.5 h-3.5" />
                </span>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom - trust & copyright */}
        <div className="space-y-3 login-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center gap-6 text-slate-400/80 text-xs">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" style={{ color: theme.primary }} />
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: theme.primary }} />
              <span>99.9% Uptime</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Globe2 className="w-3.5 h-3.5" style={{ color: theme.primary }} />
              <span>{displayBranding.custom_domain || 'radiuslink.id'}</span>
            </div>
          </div>
          <p className="text-slate-500 text-xs dark:text-slate-400">
            © 2026 {isBrandingReady ? (displayBranding.company_name || 'RadiusLink') : ''}{isBrandingReady ? '. All rights reserved.' : ' '}
          </p>
        </div>
      </div>

      {/* RIGHT PANEL — Login Form */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-8 py-8 lg:py-0">
        <div
          className="w-full max-w-md login-fade-in"
          style={{ animationDelay: '0.15s' }}
        >
          {/* Mobile-only brand header */}
          <div className="lg:hidden text-center mb-6">
            {!isBrandingReady ? (
              <>
                <div className="mx-auto h-14 sm:h-16 w-[180px] rounded-xl bg-white/[0.04]" />
                <div className="mx-auto h-5 w-40 mt-3 rounded bg-white/[0.04]" />
                <div className="mx-auto h-3 w-56 mt-2 rounded bg-white/[0.03]" />
              </>
            ) : (
              <>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={displayBranding.company_name || 'Logo'}
                    className="mx-auto h-14 sm:h-16 w-auto max-w-[220px] object-contain"
                    style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.4))' }}
                    data-testid="mobile-login-tenant-logo"
                  />
                ) : (
                  <div
                    className="inline-flex w-16 h-16 rounded-2xl items-center justify-center shadow-2xl"
                    style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}
                  >
                    <Wifi className="w-8 h-8 text-white" />
                  </div>
                )}
                <h2 className="text-white font-bold text-xl mt-3">{brandTitle}</h2>
                <p className="text-slate-400 text-xs mt-1 dark:text-slate-500">{brandTagline}</p>
              </>
            )}
          </div>

          {/* Card */}
          <div
            className="relative rounded-3xl p-7 sm:p-9 overflow-hidden"
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow:
                '0 30px 80px -20px rgba(0, 0, 0, 0.6), inset 0 1px 0 0 rgba(255,255,255,0.08)',
            }}
          >
            {/* Top accent bar */}
            <div
              className="absolute top-0 left-0 right-0 h-1"
              style={{
                background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent}, ${theme.primary})`,
              }}
            />

            <div className="mb-6 hidden lg:block">
              <h2 className="text-white text-2xl font-bold tracking-tight">
                {isSuperAdmin ? 'Super Admin Portal' : 'Welcome Back'}
              </h2>
              <p className="text-slate-400 text-sm mt-1 dark:text-slate-500">
                {isSuperAdmin ? 'Akses panel administrasi platform.' : 'Silakan masuk untuk melanjutkan ke dashboard.'}
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Username */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 mb-2">
                  Username
                </label>
                <div className="relative group">
                  <User
                    className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:opacity-80 transition-opacity dark:text-slate-500"
                    style={{ color: theme.primary }}
                  />
                  <input
                    data-testid="username-input"
                    type="text"
                    placeholder="username atau email"
                    value={loginData.username}
                    onChange={(e) => {
                      setLoginData({ ...loginData, username: e.target.value });
                      checkIfSuperAdmin(e.target.value);
                    }}
                    required
                    className="w-full h-12 pl-11 pr-4 text-sm rounded-xl bg-white/5 text-white placeholder:text-slate-500 border border-white/10 focus:bg-white/[0.08] focus:outline-none focus:ring-2 transition-all"
                    style={{
                      '--tw-ring-color': `${theme.primary}55`,
                      borderColor: 'rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 mb-2">
                  Password
                </label>
                <div className="relative group">
                  <Lock
                    className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 transition-opacity"
                    style={{ color: theme.primary }}
                  />
                  <input
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="w-full h-12 pl-11 pr-12 text-sm rounded-xl bg-white/5 text-white placeholder:text-slate-500 border border-white/10 focus:bg-white/[0.08] focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': `${theme.primary}55` }}
                    onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors p-1.5 rounded-md hover:bg-white/5 dark:text-slate-500"
                    data-testid="toggle-password"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Sign in button */}
              <button
                data-testid="login-btn"
                type="submit"
                disabled={loading}
                className="w-full h-12 mt-2 rounded-xl text-white text-sm font-bold tracking-wide flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-95 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed shadow-2xl"
                style={{
                  background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent} 100%)`,
                  boxShadow: `0 12px 32px -8px ${theme.primary}80`,
                }}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    <span>SIGN IN</span>
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-7 pt-5 border-t border-white/10 text-center">
              <p className="text-slate-400/70 text-[11px]">
                Dilindungi oleh enkripsi SSL · Versi 2.0
              </p>
            </div>
          </div>

          {/* Powered by - only mobile */}
          <div className="lg:hidden mt-6 text-center">
            <div className="inline-flex items-center gap-2 text-[11px] text-slate-400/70">
              <span>Powered by</span>
              <img src="/radiuslink-logo.png" alt="RadiusLink" className="h-3.5 w-auto opacity-80" />
              <span>v2.0</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating "Powered by" desktop */}
      <div className="hidden lg:flex absolute bottom-5 right-8 z-20 items-center gap-2 text-[11px] text-slate-400/70">
        <span>Powered by</span>
        <img src="/radiuslink-logo.png" alt="RadiusLink" className="h-4 w-auto opacity-90" />
        <span className="opacity-70">·</span>
        <span className="opacity-70">v2.0</span>
      </div>

      <style>{`
        @keyframes login-fade-in {
          0%   { opacity: 0; transform: translateY(16px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .login-fade-in {
          animation: login-fade-in 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
      `}</style>
    </div>
    </>
  );
};

export default Login;
